/* eslint-disable no-console */
import React from 'react'
import {
  BaseComponent,
  RadioFormField,
  makeForm,
} from 'belle-js'
import { compose } from 'redux'
import ContactDropdown from './ContactDropdown/ContactDropdown'
import withFormResponseHandler from '../../withFormResponseHandler'
import classnames from 'classnames'
import { objectIsEmpty } from 'utils/Client/storeUtils'
import {
  beneficiaryTypes,
  contactFormTypes,
} from 'utils/contactUtils'
import { contactComplete } from 'utils/contactTypes'
import PrivateWealthEmailLink from 'components/Shared/PrivateWealthEmailLink/PrivateWealthEmailLink'

import styles from './ContactIndexForm.module.scss'

const dropdownMap = {
  dropdowns: 'Primary',
  contingentDropdowns: 'Contingent',
  successorDonorDropdowns: 'Successor Donor',
}

class ContactIndexForm extends BaseComponent {

  constructor (props) {
    super(props)
    this.state = {
      dropdowns: [],
      contingentDropdowns: [],
      successorDonorDropdowns: [],
    }
    this.bind(
      'handleDelete',
      'handleDropdownChange',
      'setupDropdowns',
      'preserveDropdownOrder',
      'handleOpenContingentBeneficiaryModal',
      'getRelationshipError',
    )
  }

  getRelationshipError () {
    const { isSuccessorDonor } = this.props
    const errorMessageClass = classnames(styles.errorMessage, styles.errorColor)
    const subject =  isSuccessorDonor ? 'successor donor' : 'beneficiary'
    const sectionClasses = [styles.resultSection]
    if (isSuccessorDonor) sectionClasses.push(styles.singleContact)

    return (
      <div className={classnames(...sectionClasses)}>
        <span className={errorMessageClass}>
          Please ensure you add a relationship for each {subject}.
          Click on “Add Details” to add.
        </span>
      </div>
    )
  }

  setupDropdowns () {
    const { adHocContacts, isBeneficiaryForm } = this.props
    const {
      defaultDropdownQuantity,
      contingentDropdownQuantity,
    } = this.getDropdownQuantity()

    let defaultContacts = adHocContacts
    let contingentDropdowns = []

    if (isBeneficiaryForm) {
      const contingentContacts =
        this.filterContactsByBeneficiaryType(adHocContacts, beneficiaryTypes.CONTINGENT)

      contingentDropdowns = this.populateDropdowns(contingentDropdownQuantity, contingentContacts)

      defaultContacts =
        this.filterContactsByBeneficiaryType(adHocContacts, beneficiaryTypes.PRIMARY)
    }

    const dropdowns = this.populateDropdowns(defaultDropdownQuantity, defaultContacts)

    const successorDonorContacts = adHocContacts.filter(c => c.role === 'Successor Donor')
    const successorDonorDropdowns = this.populateDropdowns(1, successorDonorContacts)

    this.setState(previousState => ({
      dropdowns: this.preserveDropdownOrder(
        previousState.dropdowns,
        dropdowns
      ),
      contingentDropdowns: this.preserveDropdownOrder(
        previousState.contingentDropdowns,
        contingentDropdowns
      ),
      successorDonorDropdowns: this.preserveDropdownOrder(
        previousState.successorDonorDropdowns,
        successorDonorDropdowns,
      ),
    }))
  }

  preserveDropdownOrder (previousDropdowns, newDropdowns) {
    if (previousDropdowns.length === 0) {
      return newDropdowns
    }

    let previousDropdownsCopy = [...previousDropdowns]
    const newContactDropdowns = newDropdowns.filter(d => !!d)
    const previousContactDropdowns = previousDropdowns.filter(d => !!d)

    const previousDropdownIds = previousContactDropdowns.map(d => d.id)
    const newDropdownIds = newContactDropdowns.map(d => d.id)

    const newDropdown = newContactDropdowns.find(d => !previousDropdownIds.includes(d.id))
    const removedDropdown = previousContactDropdowns.find(d => !newDropdownIds.includes(d.id))
    const newDropdownEmpties = newDropdowns.filter(d => !d)
    const removedDropdownIndex = previousDropdowns.indexOf(removedDropdown)

    if (removedDropdown && !newDropdown) {
      previousDropdownsCopy.splice(removedDropdownIndex, 1)
      if (previousDropdownsCopy.every(d => !!d)) {
        previousDropdownsCopy = [...previousDropdownsCopy, null]
      }
    }

    if (removedDropdown && newDropdown) {
      previousDropdownsCopy[removedDropdownIndex] = newDropdown
    }

    if (!removedDropdown && newDropdown) {
      previousDropdownsCopy[previousDropdowns.length - 1] = newDropdown
      previousDropdownsCopy = [...previousDropdownsCopy, ...newDropdownEmpties]
    }

    return previousDropdownsCopy
  }

  componentDidMount () {
    this.setupDropdowns()
  }

  getDropdownStateType (type) {
    return type === beneficiaryTypes.CONTINGENT ? 'contingentDropdowns' : 'dropdowns'
  }

  populateDropdowns (dropdownQuantity, adHocContacts) {
    return Array(dropdownQuantity)
      .fill(null)
      .map((dropdown, index) => {
        if (adHocContacts[index]) {
          return adHocContacts[index]
        }
        return dropdown
      })
  }

  async handleDelete (dropdownIndex, type, contactId) {
    const {
      onDeleteContact,
      adHocContacts
    } = this.props

    const isAlreadyAContact = adHocContacts.find(e => e.id === contactId)

    isAlreadyAContact && await onDeleteContact(contactId)

    this.setupDropdowns()
  }

  getDropdownOptions () {
    const { contacts } = this.props
    return contacts.map(contact => (
      {
        label: contact.name,
        value: contact.name,
        id: contact.id
      }
    ))
  }

  filterContactsByBeneficiaryType (contacts, type) {
    return contacts.filter(p => p.beneficiaryType === type)
  }

  getDropdownQuantity () {
    const {
      isBeneficiaryForm,
      maxAdHocContacts,
      adHocContacts,
      contacts
    } = this.props

    const adHocContactIds = adHocContacts.map(a => a.id)
    const contactOptions = contacts.filter(c => !adHocContactIds.includes(c.id))
    const atLeastOneContactOption = contactOptions.length >= 1
    const adHocContactsFewerThanMax = adHocContacts.length < maxAdHocContacts
    let dropdownQuantity = adHocContacts.length

    if (adHocContactsFewerThanMax && atLeastOneContactOption) {
      dropdownQuantity = adHocContacts.length + 1
    } else if (!adHocContactsFewerThanMax) {
      dropdownQuantity = maxAdHocContacts
    }

    if (!isBeneficiaryForm) return { defaultDropdownQuantity: dropdownQuantity }

    const contingentContactsLength =
      this.filterContactsByBeneficiaryType(adHocContacts, beneficiaryTypes.CONTINGENT).length
    const primaryBeneficiaryContactsLength = adHocContacts.length - contingentContactsLength

    const maxContactsPopulated = adHocContacts.length === maxAdHocContacts
    const getTotalDropdowns = (contactsLength) => {
      if (maxContactsPopulated) {
        return contactsLength
      }
      return contactsLength + 1
    }
    const defaultDropdownQuantity = getTotalDropdowns(primaryBeneficiaryContactsLength)

    const contingentDropdownQuantity = getTotalDropdowns(contingentContactsLength)

    return {
      defaultDropdownQuantity,
      contingentDropdownQuantity,
    }
  }

  async handleDropdownChange (dropdownIndex, type, formData, contactName) {
    const dropdownType = this.getDropdownStateType(type)
    const { onDeleteContact, onCreateContact, contacts } = this.props

    const newContact = contacts.find(c => c.name === contactName)
    const previousContact = this.state[dropdownType][dropdownIndex]
    await onCreateContact({
      contactId: newContact.id,
      beneficiaryType: dropdownMap[dropdownType]
    })

    if (previousContact) {
      await onDeleteContact(previousContact.id)
    }

    this.setupDropdowns()
  }


  renderDropdowns (formType = 'Default') {
    const {
      financialAccountType,
      financialAccountId,
      isBeneficiaryForm,
      contactType,
      formData,
      adHocContacts,
      selfContactId,
      isSuccessorDonor,
    } = this.props

    const dropdownType = this.getDropdownStateType(formType)
    const stateDropdowns = this.state[dropdownType]
    const options = this.getDropdownOptions()

    const selectedContacts = Object.values(formData)
    const takenContacts = [...selectedContacts]
    const filteredOptions = options.filter(o => !takenContacts.includes(o.value))
    const dropdowns = isSuccessorDonor ? stateDropdowns.slice(0, 1) : stateDropdowns

    return dropdowns.map((contact, index) => {
      const key = `contactName_${index}_${formType}`
      let dropdownProps = {
        key,
        filteredOptions,
        showAdditionalFields: isBeneficiaryForm && !isSuccessorDonor,
        dropdownName: `contact_${index}`,
        formType,
        onChange: this.handleDropdownChange.bind(this, index, formType, formData),
        onDelete: this.handleDelete.bind(this, index, formType)
      }

      if (contact) {
        const urlPayload = `${financialAccountType}/${financialAccountId}/${contactType}/${contact.id}`
        const editPath = `/onboarding/${urlPayload}/edit`
        const adHocContactPersisted = !!adHocContacts.find(c => c.id === contact.id)
        const isFirstDropdown = index === 0

        dropdownProps = {
          ...dropdownProps,
          contact,
          editPath,
          isFirstDropdown,
          showEditOptions: contact.id === selfContactId ? false : adHocContactPersisted,
          shouldRenderEdit: contactComplete(contactType, contact)
        }
      }

      return (
        <div key={key} >
          <ContactDropdown {...dropdownProps} />
          { (isSuccessorDonor && contact && !contact.relationship) && this.getRelationshipError() }
        </div>
      )
    })
  }

  renderContingentForm () {
    return (
      <React.Fragment>
        { this.renderDropdowns(beneficiaryTypes.CONTINGENT) }
        { this.getTotalSection(beneficiaryTypes.CONTINGENT) }
      </React.Fragment>
    )
  }

  renderCreateNewContact (params) {
    const {
      shouldDisableBtn,
      addNewContactClass,
      onAddContact,
      maxContactsReached,
      beneficiaryType,
      singleContact,
    } = params

    const {
      contactType
    } = this.props

    const contactName = contactType.split('_').join(' ')

    const maxContactsReachedText =
      contactType === 'trustees' ?
        (
          <span>
          You can only add 10 {contactName} using this form.
            If you want to add more trustees, please <PrivateWealthEmailLink message="contact your team" />. 
          </span>
        ) :
        (
          <span>
            {`You’ve added the maximum number of ${contactName}.`}
          </span>
        )

    return (
      <div className={styles.buttonRow}>
        <button
          type="button"
          disabled={shouldDisableBtn}
          className={addNewContactClass}
          onClick={onAddContact.bind(this, { beneficiaryType })}>
          Create New Contact
        </button>
        { maxContactsReached && !singleContact &&
          <div className={styles.row}>
            <div className={styles.maxContactsReached} >
              {maxContactsReachedText}
            </div>
          </div>}
      </div>
    )
  }

  getContactsSection () {
    const {
      onAddContact,
      maxAdHocContacts,
      adHocContacts,
      contacts,
      formData,
      sectionTitle,
      isBeneficiaryForm,
      isSuccessorDonor,
    } = this.props

    const { dropdowns, contingentDropdowns } = this.state
    const defaultDropdownLength = dropdowns.filter(d => d).length
    const contingentDropdownLength = contingentDropdowns.filter(d => d).length
    const maxContactsReached = defaultDropdownLength + contingentDropdownLength >= maxAdHocContacts
    const hasContingentContacts =
      this.filterContactsByBeneficiaryType(adHocContacts, beneficiaryTypes.CONTINGENT).length
    const hasBeneficiariesContacts =
      this.filterContactsByBeneficiaryType(adHocContacts, beneficiaryTypes.PRIMARY).length

    const addNewContactClass = classnames(
      'button',
      'button--secondary',
      styles.primaryCTA,
      styles.noBtnStyles,
      styles.displayFlex
    )
    const shouldRenderContingentForm = isBeneficiaryForm &&
      (hasContingentContacts || formData.contingencyBeneficiary === 'Yes')

    const params = {
      shouldDisableBtn: false,
      addNewContactClass,
      onAddContact,
      maxContactsReached,
      singleContact: maxAdHocContacts === 1,
    }

    const title = isBeneficiaryForm ? `Primary ${sectionTitle}` : sectionTitle

    return (
      <div>
        <p className={styles.sectionTitle}>{title}</p>
        { this.renderDropdowns() }
        { (isBeneficiaryForm && !isSuccessorDonor) && this.getTotalSection('Default') }
        { this.renderCreateNewContact({ ...params, beneficiaryType: isBeneficiaryForm ? 'primary' : null })}
        { (isBeneficiaryForm && contacts.length && !isSuccessorDonor) &&
          this.renderContingentBeneficiary(hasContingentContacts, hasBeneficiariesContacts) }
        { (shouldRenderContingentForm && !isSuccessorDonor) && this.renderContingentForm() }
        { (shouldRenderContingentForm && !isSuccessorDonor) &&  this.renderCreateNewContact({ ...params, beneficiaryType: 'contingent' }) }
      </div>
    )
  }

  renderCTAs () {
    const baseClasses = ['button', 'button--form']
    const standardButtonClass = classnames(
      ...baseClasses,
      'button--primary',
    )
    const primaryButtonClass = classnames(
      ...baseClasses,
      'button--secondary',
      styles.buttonMedium
    )

    const secondaryButtonClass = classnames(
      standardButtonClass,
      styles.buttonMedium,
    )

    const {
      formData,
      goNextSection,
      isBeneficiaryForm,
      isSuccessorDonor,
    } = this.props
    const { dropdowns: defaultDropdowns, successorDonorDropdowns } = this.state
    const dropdowns = isSuccessorDonor ? successorDonorDropdowns : defaultDropdowns
    const totalPercentages = contactFormTypes.map((formType) => {
      const hasFormType = Object.keys(formData).find(k => k.includes(formType))
      if (hasFormType) {
        return this.calculateTotalPercentage(formData, formType)
      }
      return 100
    })

    const individualsHaveRelationships = dropdowns
      .every(contact => !contact || 
        (!!contact.firstName && !!contact.relationship) || (!contact.firstName))
      // !contact -> ignore the null contact at the end
      // !contact.firstName --> the contact is not an individual
    const totalPercentageAddsUp = totalPercentages.every(p => p === 100)
    const contactHasZeroPercent = totalPercentageAddsUp && contactFormTypes
      .map(t => this.anyContactPercentageZero(t))
      .some(p => p)
    const atLeastOneContact = dropdowns.some(e => !!e)
    const isInvalidBeneficiary = (
      !totalPercentageAddsUp ||
      !atLeastOneContact ||
      contactHasZeroPercent ||
      !individualsHaveRelationships
    )
    const disableSave = (isBeneficiaryForm && !isSuccessorDonor) ? isInvalidBeneficiary : false
    const saveAndSubmitStyles = classnames(secondaryButtonClass, styles.makeFirstInMobile, {
      'is-disable': disableSave
    })

    return (
      <div className={styles.ctas}>
        <button
          type="button"
          className={classnames(primaryButtonClass, styles.makeSecondInMobile)}
          onClick={goNextSection}>
         Do this later
        </button>
        <button
          disabled={disableSave}
          className={saveAndSubmitStyles}
          type="submit" >
         Save &amp; Continue
        </button>
      </div>
    )
  }

  getContactPercentages (formData, type) {
    return Object.keys(formData)
      .filter(k => k.includes('_percentage') && k.includes(type))
      .reduce((percentageKeys, propertyKey) => {
        const propertyValue = formData[propertyKey]
        return {
          ...percentageKeys,
          [propertyKey]: propertyValue
        }
      }, {})
  }

  getPercentagesSum (contactWithPercentages) {
    return Object.values(contactWithPercentages).reduce((total, percentage) => {
      if  (isNaN(parseFloat(percentage))) {
        return total
      }
      return parseFloat(percentage) + total
    }, 0.0)
  }

  calculateTotalPercentage (formData, type) {
    const contactPercentages = this.getContactPercentages(formData, type)
    return objectIsEmpty(formData) ?
      100 : this.getPercentagesSum(contactPercentages)
  }

  anyContactPercentageZero (type = 'Default') {
    const { formData } = this.props
    let present = false
    Object.keys(formData).filter(f => f.includes('percentage') && f.includes(type))
      .forEach((p) => { if (formData[p] === 0 || formData[p] === '') present = true })
    return present
  }

  getTotalSection (type) {
    const { formData } = this.props
    const dropdownType = this.getDropdownStateType(type)
    const dropdowns = this.state[dropdownType]
    const totalPercentage = this.calculateTotalPercentage(formData, type)
    const isFirstDropdownEmpty = !dropdowns[0]
    const totalPercentageAddsUp = totalPercentage === 100 || isFirstDropdownEmpty
    const contactHasZeroPercent = totalPercentageAddsUp && this.anyContactPercentageZero(type)
    const individualsHaveRelationships = dropdowns.every(contact => 
      !contact || 
      (!!contact.firstName && !!contact.relationship) ||
      (!contact.firstName)
    )
    // !contact -> ignore the null contact at the end
    // !contact.firstName --> the contact is not an individual
    const totalPercentageClass = classnames(styles.totalPercentage, {
      [styles.errorColor]: !totalPercentageAddsUp || (contactHasZeroPercent)
    })
    const errorMessageClass = classnames(styles.errorMessage, styles.errorColor)

    return (
      <div className={styles.resultSection}>
        <span className={styles.totalLabel}>Total</span>
        <span className={totalPercentageClass}>{totalPercentage}%</span>
        { !totalPercentageAddsUp &&
          <span className={errorMessageClass}>
            Share percentages must add up to 100%.
          </span>
        }
        { contactHasZeroPercent &&
          <span className={errorMessageClass}>
            all beneficiaries must receive a positive value.
          </span>
        }
        { !individualsHaveRelationships && 
          <span className={errorMessageClass}>
            Please ensure you add a relationship for each beneficiary. <br />
            Click on “Add Details” to add.
          </span>
        }
      </div>
    )
  }

  handleOpenContingentBeneficiaryModal () {
    const {
      openModal
    } = this.props

    return openModal('contingentBeneficiaryModal')
  }

  renderContingentBeneficiary (hasContingentContacts, hasBeneficiariesContacts) {
    const {
      formData: {
        contingencyBeneficiary
      },
    } = this.props

    const contingentOptions = [{
      label: 'Yes',
      value: 'Yes',
      disabled: false,
    }, {
      label: 'No',
      value: 'No',
      disabled: false,
    }]
    let value = contingencyBeneficiary ? contingencyBeneficiary : 'No'
    const contingentQuestion = 'Would you like to add a contingent beneficiary?'

    if (!hasBeneficiariesContacts) {
      contingentOptions[0].disabled = true
      value = 'No'
    } else if (hasContingentContacts) {
      contingentOptions[1].disabled = true
      value = 'Yes'
    }

    return (
      <div className={styles.contingentSection}>
        <p className={styles.contactTitle}>contingent beneficiary</p>
        <span className={styles.contingentQuestion}>
          {contingentQuestion}
          <a
            className={styles.moreSection}
            onClick={this.handleOpenContingentBeneficiaryModal}>
          MORE
          </a>
        </span>
        <div className={styles.contingencyAnswerSection}>
          <RadioFormField
            label="CONTINGENT BENEFICIARY"
            className={styles.contingentRadio}
            name="contingencyBeneficiary"
            horizontal={false}
            block={false}
            options={contingentOptions}
            required={true}
            value={value} />
        </div>
      </div>
    )
  }


  renderNoContactsSection () {
    const {
      onAddContact,
      isBeneficiaryForm
    } = this.props
    const addBtnClass = classnames(
      'button',
      'button--secondary',
      styles.buttonFullWidth
    )
    const noDataClass = classnames({
      [styles.withSubtitleMargin]: isBeneficiaryForm
    },
    styles.noData
    )

    return (
      <div className={noDataClass}>
        <p>
          You haven’t created any contacts.
          Create a new contact and you’ll be able to select them.
        </p>
        <button
          type="button"
          className={addBtnClass}
          onClick={onAddContact}>
          Create New Contact
        </button>
      </div>
    )
  }

  render () {
    const { contacts } = this.props
    return (
      <div className={styles.ContactIndexForm}>
        { contacts.length ? this.getContactsSection() : this.renderNoContactsSection()  }
        { this.renderCTAs() }
      </div>
    )
  }

}

export default compose(
  makeForm,
  withFormResponseHandler,
)(ContactIndexForm)



// WEBPACK FOOTER //
// ./src/components/Onboarding/Accounts/ContactIndexForm/ContactIndexForm.jsx